/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser'

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_TENANT_ID}`,
    redirectUri: `${window.location.origin}/login`,
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Info:
            console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
        }
      },
    },
  },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  // Graph-only scopes for profile/bootstrap.
  // Backend API token must be requested separately via backendRequest.
  scopes: [
    'User.Read',
  ].filter(Boolean),
}

// Scope for this application's backend API.
// REACT_APP_BACKEND_SCOPE must be set explicitly in .env.
// Do NOT fall back to REACT_APP_CLIENT_ID — that is the FE app and has no exposed API.
const backendScope = process.env.REACT_APP_BACKEND_SCOPE;
if (!backendScope) {
  console.error(
    '[authConfig] REACT_APP_BACKEND_SCOPE is not set. ' +
    'Token requests for the backend API will fail with AADSTS500011. ' +
    'Set REACT_APP_BACKEND_SCOPE=api://<backend-client-id>/access_as_user in your .env file.'
  );
}

export const backendRequest = {
  scopes: [backendScope || `api://${process.env.REACT_APP_BACKEND_CLIENT_ID}/access_as_user`],
};

// Scope for ServiceNow backend (separate app registration)
export const snowBackendRequest = {
  scopes: ['api://70783e6d-811b-4ce8-8ea6-65b4fc3d528b/access_as_user'],
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphProfilePhotoEndpoint: 'https://graph.microsoft.com/v1.0/me/photo/$value',
}
